import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';
import { AsyncReturnType } from '~/typings/utils';
import { useMountedState } from './use-mounted-state';

type AsyncState<T> =
  | {
      loading: true;
      error?: undefined;
      value?: undefined;
    }
  | {
      loading: false;
      error: Error;
      value?: undefined;
    }
  | {
      loading: false;
      error?: undefined;
      value: T;
    };

type FunctionReturningPromise = (...args: any[]) => Promise<any>;

type StateFromFunctionReturningPromise<T extends FunctionReturningPromise> = AsyncState<
  AsyncReturnType<T>
>;

const useAsyncFunction = <T extends FunctionReturningPromise>(
  fn: T,
  initialState: StateFromFunctionReturningPromise<T>,
  deps: DependencyList = [],
): [StateFromFunctionReturningPromise<T>, T] => {
  const lastCallId = useRef(0);
  const isMounted = useMountedState();
  const [state, setState] = useState<StateFromFunctionReturningPromise<T>>(initialState);

  const callback = useCallback((...args: Parameters<T>): ReturnType<T> => {
    const callId = ++lastCallId.current;

    if (!state.loading) {
      setState({ loading: true });
    }

    return fn(...args).then(
      value => {
        if (isMounted() && callId === lastCallId.current) setState({ value, loading: false });

        return value;
      },
      error => {
        if (isMounted() && callId === lastCallId.current) setState({ error, loading: false });

        return error;
      },
    ) as ReturnType<T>;
  }, deps);

  return [state, callback as unknown as T];
};

export const useAsync = <T extends FunctionReturningPromise>(fn: T, deps: DependencyList = []) => {
  const [state, callback] = useAsyncFunction(fn, { loading: true }, deps);

  useEffect(() => {
    callback();
  }, [callback]);

  return state;
};
